import React from 'react';

import { Icon } from '@rbilabs/components-library';
import { isNil } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { isFalse } from 'utils';

import ListOfLinks from 'components/app-header/dropdown/list-of-links';
import { ArrowLink } from 'components/arrow-link';
import { useFeatureAccountPage } from 'components/features/account-page/use-feature-account-page';
import LanguageSelector from 'components/language-selector';
import AccountLayout from 'components/layout/account';
import { brandFont } from 'components/layout/brand-font';
import Link from 'components/link';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { VerifyEmailMessage } from 'components/verify-email-message';
import useAuth from 'hooks/auth/use-auth';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import useMediaQuery from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import useEmergencyBannerPageComponent from 'state/emergency-banner/use-emergency-banner-page-component';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { MyAccountSections, defaultMyAccountSections } from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import Disclaimer from '../about/disclaimer';
import { languageSelectorValidForRegion } from '../about/languageSelectorValidForRegion';

import CommPreferenceLink from './comm-preference-link';
import {
  ACCOUNT_DISPLAY_SIGN_UP_HEADLINE,
  ACCOUNT_LINKS_BORDER_COLOR,
  ACCOUNT_LINKS_DISPLAY_LANGUAGE_SELECTOR,
  ACCOUNT_LINKS_DISPLAY_SECTION_HEADINGS,
  isAccountActionsEnabled,
} from './constants';
import CreateAccount from './create-account';
import RewardsLink from './rewards-link';
import SubmitFeedbackLink from './submit-feedback-link';

const AccountAction = styled(Link)`
  text-decoration: none;
  font: ${brandFont.headerThree};
  display: flex;
  margin: ${primitive.$spacing2} 0;
`;

const StyledIcon = styled.div`
  position: relative;
  right: ${primitive.$spacing1};
`;

const StyledBackArrowHistory = styled(BackArrowHistory)`
  margin: 0rem;
`;

const AccountLanguageSelectorContainer = styled.div`
  border-bottom: 1px solid ${p => p.theme.token('border-color-default')};
  padding-bottom: ${primitive.$spacing4};
`;

const LanguageSelectIntro = styled.div`
  text-transform: none;
`;

const ActionsWrapper = styled.div`
  border-bottom: 1px solid ${ACCOUNT_LINKS_BORDER_COLOR};
  padding-bottom: ${primitive.$spacing4};
`;

const AccountLinksWrapper = styled.div`
  margin-bottom: ${primitive.$spacing7};
`;

const Account = ({ className }: { className?: string }) => {
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const enableAutoSignInOnSignUp = useFlag(LaunchDarklyFlag.ENABLE_AUTO_SIGN_IN_ON_SIGN_UP);
  const isUnifiedAuthEntry = useFlag(LaunchDarklyFlag.EXP_UNIFIED_AUTH_ENTRY_WEB);
  const enableRequestMyInformation = useFlag(LaunchDarklyFlag.ENABLE_REQUEST_MY_INFORMATION);
  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;
  const enableCommPrefFlag = useFlag(LaunchDarklyFlag.ENABLE_COMMUNICATION_PREFERENCES);
  const isLocalizationDisabled = useFlag(LaunchDarklyFlag.DISABLE_LOCALIZATION);
  const enableDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_DIAGNOSTIC_TOOLS);
  const enableNativeMobileAccount = useFlag(LaunchDarklyFlag.ENABLE_NATIVE_MOBILE_ACCOUNT);
  const enableFindRestaurantLink = useFlag(LaunchDarklyFlag.ENABLE_FIND_RESTAURANT_LINK);
  const enablePayback = useFlag(LaunchDarklyFlag.ENABLE_PAYBACK);
  const isMobile = useMediaQuery('mobile');
  const enableCommunicationPreferences = !isFalse(enableCommPrefFlag);
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  const { featureAccountPage } = useFeatureAccountPage();

  const showAboutMenuLinks = enableNativeMobileAccount && isMobile;
  useAuthRedirects({ skip: showAboutMenuLinks });

  const { featureAboutMenuEntriesMobile: aboutMenuEntries }: any = useFeatureNavigation();

  const navigate = useNavigate();

  const showLanguageSelector =
    !isLocalizationDisabled &&
    languageSelectorValidForRegion &&
    ACCOUNT_LINKS_DISPLAY_LANGUAGE_SELECTOR;

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;
  const enabledAccountSections =
    useFlag<MyAccountSections>(LaunchDarklyFlag.ENABLE_ACCOUNT_SECTIONS_VARIATIONS) ||
    defaultMyAccountSections;

  const isMobileAccountSignUp = isMobile && ACCOUNT_DISPLAY_SIGN_UP_HEADLINE;
  const showCreateAccount = isMobileAccountSignUp && !isAuthenticated();
  const showSignUpLink = !isMobileAccountSignUp && !isAuthenticated();
  const isHamburgerMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_HAMBURGER_MENU_ON_MOBILE);

  useEmergencyBannerPageComponent(featureAccountPage?.components || []);

  if (!showAboutMenuLinks && !isAuthenticated()) {
    return <div />;
  }

  return (
    <div>
      <Helmet title={formatMessage({ id: 'myAccount' })} />
      <AccountLayout className={className}>
        {isHamburgerMenuEnabled && (
          <StyledBackArrowHistory
            icon={<Icon icon="close" width="24px" color="icon-header-contrast" aria-hidden />}
          />
        )}
        {isAuthenticated() && (
          <AccountLinksWrapper>
            {(enableAutoSignInOnSignUp || isUnifiedAuthEntry) &&
              user?.details.emailVerified === false && <VerifyEmailMessage />}
            <RewardsLink />
            {enabledAccountSections.accountInfo && (
              <ArrowLink to={routes.info} data-testid="account-info">
                {formatMessage({ id: 'accountInfo' })}
              </ArrowLink>
            )}
            {enabledAccountSections.earnPoints && (
              <ArrowLink
                to={isMobile ? routes.redeem : formatMessage({ id: 'routes.linkPhysicalCard' })}
                data-testid="earn-points"
              >
                {formatMessage({ id: 'earnPoints' })}
              </ArrowLink>
            )}
            {enablePayback && (
              <ArrowLink to={routes.payback} data-testid="payback">
                {formatMessage({ id: 'payback' })}
              </ArrowLink>
            )}
            {enabledAccountSections.paymentMethods && enableOrdering && (
              <ArrowLink to={routes.payment} data-testid="payment-methods-url">
                {formatMessage({ id: 'paymentMethods' })}
              </ArrowLink>
            )}
            {enabledAccountSections.recentOrders && enableOrdering && (
              <ArrowLink to={routes.orders} data-testid="recent-orders">
                {formatMessage({ id: 'recentOrders' })}
              </ArrowLink>
            )}
            {enableCommunicationPreferences && <CommPreferenceLink />}
            {enabledAccountSections.submitFeedback && <SubmitFeedbackLink />}
            {enableRequestMyInformation && (
              <ArrowLink to={formatMessage({ id: 'routes.accountRequestInfo' })}>
                {formatMessage({ id: 'requestMyInformation' })}
              </ArrowLink>
            )}
            {enabledAccountSections.userPreferences && (
              <ArrowLink to={formatMessage({ id: 'routes.userPreferencesModal' })}>
                {formatMessage({ id: 'userPreferences' })}
              </ArrowLink>
            )}
          </AccountLinksWrapper>
        )}

        {showAboutMenuLinks && (
          <>
            {showLanguageSelector && (
              <AccountLanguageSelectorContainer>
                <LanguageSelectIntro>
                  {formatMessage({ id: 'languageAndRegion' })}
                </LanguageSelectIntro>
                <LanguageSelector color={theme.token('text-default')} />
              </AccountLanguageSelectorContainer>
            )}

            {isAccountActionsEnabled(enableSignUp) && (
              <ActionsWrapper>
                {showCreateAccount && <CreateAccount />}
                {showSignUpLink && (
                  <AccountAction data-testid="signup-link" to={routes.signUp}>
                    <StyledIcon>
                      <Icon icon="account" color="icon-default" width="24px" aria-hidden />
                    </StyledIcon>
                    <span>{formatMessage({ id: 'signInOrCreateAccount' })}</span>
                  </AccountAction>
                )}
                {enableFindRestaurantLink && (
                  <AccountAction to={routes.storeLocator}>
                    <StyledIcon>
                      <Icon icon="location" color="icon-default" width="24px" aria-hidden />
                    </StyledIcon>
                    <span>{formatMessage({ id: 'findARestaurant' })}</span>
                  </AccountAction>
                )}
              </ActionsWrapper>
            )}

            {aboutMenuEntries && (
              <ListOfLinks
                data-testid="links-list"
                title={aboutMenuEntries.name?.locale || ''}
                entries={aboutMenuEntries.pages || []}
                borderColor={ACCOUNT_LINKS_BORDER_COLOR}
                sectionHeadings={ACCOUNT_LINKS_DISPLAY_SECTION_HEADINGS}
                navigate={navigate}
              />
            )}
            <Disclaimer />
            {enableDiagnosticTools && (
              <Link to={routes.diagnostics}>{formatMessage({ id: 'diagnostics' })}</Link>
            )}
          </>
        )}
      </AccountLayout>
    </div>
  );
};

export default Account;
